package org.teddapp.project.ui.mindmap

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Note
import org.teddapp.project.db.ParentKind
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType

/**
 * Le nœud sélectionné : son texte, les notes écrites dedans, les notes de la
 * bibliothèque rattachées, et d'où il vient quand il est né d'une compétence.
 * Panneau latéral sur grand écran, feuille sur téléphone.
 */
@Composable
internal fun MindMapNodePanel(
    model: MindMapModel,
    nodeId: String,
    compact: Boolean,
    onOpenSource: (kind: String, id: String) -> Unit,
    onRequestDelete: (String) -> Unit,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val node = model.nodes.firstOrNull { it.id == nodeId } ?: return
    var text by remember(nodeId) { mutableStateOf(node.text) }
    // Une mise à jour venue d'ailleurs (autre appareil, édition en place) remplace ce qu'on n'a pas touché.
    if (text != node.text && node.text.isNotBlank() && text.isBlank()) text = node.text
    var picking by remember { mutableStateOf(false) }
    var openNoteId by remember(nodeId) { mutableStateOf<String?>(null) }
    val inline = model.inlineNotes(nodeId)
    val attached = model.attachedNotes(nodeId)
    val depth = model.layout.pathTo(nodeId).size - 1

    Column(
        modifier = modifier
            .verticalScroll(rememberScrollState())
            .padding(horizontal = if (compact) TeddSpace.S4 else TeddSpace.S5, vertical = TeddSpace.S4),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S4),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Text(
                if (depth == 0) "Sujet central" else "Nœud · niveau $depth",
                style = TeddType.Overline,
                color = TeddTokens.InkMuted,
                modifier = Modifier.weight(1f),
            )
            if (!compact) TeddIconButton(onClick = onClose) { TeddIcon(name = "x", size = 16.dp, color = TeddTokens.InkMuted) }
        }
        TeddField(
            label = "Texte",
            value = text,
            onValueChange = { text = it; model.renameLive(nodeId, it) },
            placeholder = "Une idée, un mot, une question",
        )
        // Les quatre gestes sur un nœud, en boutons : pas besoin de connaître les raccourcis.
        val hasChildren = model.nodes.any { it.parent_id == nodeId }
        androidx.compose.foundation.layout.FlowRow(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2), verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            TeddButton(text = "Enfant", onClick = { model.addChild(nodeId) }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "plus", kbd = if (compact) null else "Tab")
            if (node.parent_id != null) TeddButton(text = "Frère", onClick = { model.addSibling(nodeId) }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "arrow-down", kbd = if (compact) null else "⇧Tab")
            if (hasChildren) TeddButton(
                text = if (nodeId in model.collapsed) "Déplier" else "Plier",
                onClick = { model.toggleCollapse(nodeId) },
                kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = if (nodeId in model.collapsed) "unfold" else "fold",
            )
            if (node.parent_id != null) TeddButton(text = "Supprimer", onClick = { onRequestDelete(nodeId) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "trash", kbd = if (compact) null else "Suppr")
        }
        node.source_kind?.let { kind ->
            val id = node.source_id
            val label = sourceLabel(kind)
            if (label != null && id != null && kind != MindMapSources.GROUP) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                    TeddIcon(name = "link", size = 14.dp, color = TeddTokens.InkMuted)
                    Text("Issu de la compétence : $label", style = TeddType.Caption, color = TeddTokens.InkMuted, modifier = Modifier.weight(1f))
                    if (MindMapSources.canOpen(kind)) {
                        TeddButton(text = "Ouvrir", onClick = { onOpenSource(kind, id) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                    }
                }
            }
        }

        // Notes écrites dans le nœud.
        SectionTitle("Notes", inline.size) {
            TeddButton(text = "Nouvelle", onClick = { model.newInlineNote(nodeId) { openNoteId = it } }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "plus")
        }
        if (inline.isEmpty()) Hint("Écris ici ce que ce nœud t'apprend : une définition, un exemple, une question à creuser.")
        inline.forEach { note ->
            InlineNoteCard(
                note = note,
                open = openNoteId == note.id,
                onToggle = { openNoteId = if (openNoteId == note.id) null else note.id },
                onChange = { t, b -> model.updateNote(note, t, b) },
                onDelete = { model.deleteNote(note.id); if (openNoteId == note.id) openNoteId = null },
            )
        }

        // Notes de la bibliothèque rattachées.
        SectionTitle("Rattachées", attached.size) {
            TeddButton(text = "Rattacher", onClick = { picking = true }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "link")
        }
        if (attached.isEmpty()) Hint("Relie une note de tes carnets : elle reste à sa place, le nœud y mène.")
        attached.forEach { note ->
            Row(
                modifier = Modifier.fillMaxWidth()
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RMd))
                    .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S2),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
            ) {
                TeddIcon(name = "notes", size = 14.dp, color = TeddTokens.ModNotes)
                Column(Modifier.weight(1f)) {
                    Text(noteTitle(note), style = TeddType.BodyStrong, color = TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis)
                    val where = model.bookName(note.parent_id) ?: "Sans carnet"
                    Text(where, style = TeddType.Caption, color = TeddTokens.InkMuted, maxLines = 1, overflow = TextOverflow.Ellipsis)
                }
                TeddIconButton(onClick = { model.detachNote(nodeId, note.id) }) { TeddIcon(name = "x", size = 14.dp, color = TeddTokens.InkMuted) }
            }
        }
        Box(Modifier.heightIn(min = TeddSpace.S8))
    }

    if (picking) {
        NotePicker(
            model = model,
            already = attached.map { it.id }.toSet(),
            onPick = { model.attachNote(nodeId, it) },
            onClose = { picking = false },
        )
    }
}

@Composable
private fun SectionTitle(title: String, count: Int, action: @Composable () -> Unit) {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().padding(top = TeddSpace.S2)) {
        Text(title.uppercase() + if (count > 0) " · $count" else "", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.weight(1f))
        action()
    }
}

@Composable
private fun Hint(text: String) {
    Text(text, style = TeddType.Caption, color = TeddTokens.InkSubtle)
}

internal fun noteTitle(note: Note): String =
    note.title?.takeIf { it.isNotBlank() } ?: note.body.lineSequence().firstOrNull { it.isNotBlank() }?.take(60) ?: "Note vide"

/** Une note du nœud : repliée, sa première ligne ; ouverte, titre et corps modifiables. */
@Composable
private fun InlineNoteCard(note: Note, open: Boolean, onToggle: () -> Unit, onChange: (String?, String) -> Unit, onDelete: () -> Unit) {
    var title by remember(note.id) { mutableStateOf(note.title.orEmpty()) }
    var body by remember(note.id) { mutableStateOf(note.body) }
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Column(
        modifier = Modifier.fillMaxWidth()
            .background(TeddTokens.Surface, shape)
            .border(1.dp, if (open) TeddTokens.LineStrong else TeddTokens.Line, shape)
            .padding(TeddSpace.S3),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth().clickable(onClick = onToggle)) {
            TeddIcon(name = if (open) "chevron-down" else "chevron-right", size = 14.dp, color = TeddTokens.InkMuted)
            Text(
                if (open) "Note" else noteTitle(Note(note.id, note.parent_kind, note.parent_id, title.ifBlank { null }, body, note.color, note.is_pinned, note.is_favorite, note.tags, note.attachments, note.order_index, note.created_at, note.updated_at, note.is_deleted, note.deleted_at, note.synced_at)),
                style = TeddType.BodyStrong, color = TeddTokens.Ink, maxLines = 2, overflow = TextOverflow.Ellipsis,
                modifier = Modifier.weight(1f).padding(start = TeddSpace.S2),
            )
            if (open) TeddIconButton(onClick = onDelete) { TeddIcon(name = "trash", size = 14.dp, color = TeddTokens.InkMuted) }
        }
        if (open) {
            BasicTextField(
                value = title,
                onValueChange = { title = it; onChange(it, body) },
                textStyle = TeddType.BodyStrong.copy(color = TeddTokens.Ink),
                cursorBrush = SolidColor(TeddTokens.Action),
                decorationBox = { inner -> Box { if (title.isEmpty()) Text("Titre (optionnel)", style = TeddType.BodyStrong, color = TeddTokens.InkSubtle); inner() } },
                modifier = Modifier.fillMaxWidth(),
            )
            BasicTextField(
                value = body,
                onValueChange = { body = it; onChange(title, it) },
                textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
                cursorBrush = SolidColor(TeddTokens.Action),
                decorationBox = { inner -> Box { if (body.isEmpty()) Text("Ce que tu veux retenir…", style = TeddType.Body, color = TeddTokens.InkSubtle); inner() } },
                modifier = Modifier.fillMaxWidth().heightIn(min = 80.dp),
            )
        }
    }
}

/** Choisir une note de la bibliothèque à rattacher : recherche, groupée par carnet. */
@Composable
private fun NotePicker(model: MindMapModel, already: Set<String>, onPick: (String) -> Unit, onClose: () -> Unit) {
    var query by remember { mutableStateOf("") }
    val close = org.teddapp.project.ui.rememberAfterFrame(onClose)
    val candidates = remember(model.allNotes, query) {
        val q = query.trim()
        model.allNotes
            .filter { it.parent_kind != ParentKind.MindMapNode.raw }
            .filter { q.isEmpty() || (it.title ?: "").contains(q, true) || it.body.contains(q, true) }
            .sortedByDescending { it.updated_at }
            .take(200)
    }
    Dialog(onDismissRequest = close, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(480.dp)
                .background(TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RLg))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
                .padding(TeddSpace.S5),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            Text("Rattacher une note", style = TeddType.Title2, color = TeddTokens.Ink)
            TeddField(label = "Chercher", value = query, onValueChange = { query = it }, placeholder = "Titre ou contenu")
            LazyColumn(modifier = Modifier.heightIn(max = 380.dp), verticalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
                if (candidates.isEmpty()) item { Text("Aucune note ne correspond.", style = TeddType.Caption, color = TeddTokens.InkMuted) }
                items(candidates, key = { it.id }) { note ->
                    val taken = note.id in already
                    Row(
                        modifier = Modifier.fillMaxWidth()
                            .background(if (taken) TeddTokens.SurfaceSunken else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                            .clickable(enabled = !taken) { onPick(note.id); close() }
                            .padding(horizontal = TeddSpace.S3, vertical = TeddSpace.S2),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
                    ) {
                        TeddIcon(name = if (taken) "check" else "notes", size = 14.dp, color = if (taken) TeddTokens.Success else TeddTokens.ModNotes)
                        Column(Modifier.weight(1f)) {
                            Text(noteTitle(note), style = TeddType.BodyStrong, color = TeddTokens.Ink, maxLines = 1, overflow = TextOverflow.Ellipsis)
                            Text(model.bookName(note.parent_id) ?: "Sans carnet", style = TeddType.Caption, color = TeddTokens.InkMuted)
                        }
                    }
                }
            }
            TeddButton(text = "Fermer", onClick = close, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
        }
    }
}

/** Les provenances possibles d'un nœud né d'une compétence. */
internal object MindMapSources {
    const val GROUP = "group"
    fun canOpen(kind: String): Boolean = kind in setOf("skill", "parcours_step", "objective", "milestone", "task", "project", "competence", "competence_criterion", "flashcard", "parcours_level")
}

private fun sourceLabel(kind: String): String? = when (kind) {
    "skill" -> "la compétence"
    "parcours_step", "parcours_level" -> "une étape du parcours"
    "objective" -> "un objectif"
    "milestone" -> "un jalon"
    "task" -> "une tâche"
    "project" -> "un projet"
    "competence" -> "une compétence du recueil"
    "competence_criterion" -> "un critère du recueil"
    "flashcard" -> "une carte mémoire"
    MindMapSources.GROUP -> null
    else -> kind
}
